import { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

interface MarkdownProps {
  content: string;
  /** Labels for the injected code-block copy buttons. */
  copyLabel: string;
  copiedLabel: string;
}

marked.setOptions({ gfm: true, breaks: true });

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'code', 'del', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'sub', 'sup',
    'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul',
  ],
  ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'class', 'target', 'rel', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false,
};

/**
 * Post-processes the *sanitised* HTML before React writes it: links open in a
 * new tab, and every code block gets a wrapper plus a copy button.
 *
 * Doing this here rather than in an effect matters: React owns the contents of
 * `dangerouslySetInnerHTML` and rewrites them on every content change, which
 * silently undoes any DOM surgery performed afterwards.
 */
function enhance(safeHtml: string, copyLabel: string): string {
  if (!safeHtml || typeof document === 'undefined') return safeHtml;

  const scratch = document.createElement('div');
  scratch.innerHTML = safeHtml;

  for (const anchor of Array.from(scratch.querySelectorAll('a'))) {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer nofollow');
  }

  for (const pre of Array.from(scratch.querySelectorAll('pre'))) {
    const wrapper = document.createElement('div');
    wrapper.className = 'cbw-code';
    pre.parentNode?.insertBefore(wrapper, pre);
    wrapper.appendChild(pre);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cbw-code__copy';
    button.textContent = copyLabel;
    wrapper.appendChild(button);
  }

  return scratch.innerHTML;
}

/**
 * Renders assistant markdown. Content originates from a model, so it is parsed
 * then sanitised before it ever reaches the DOM.
 */
export function Markdown({ content, copyLabel, copiedLabel }: MarkdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const html = useMemo(() => {
    if (!content) return '';
    let parsed: string;
    try {
      parsed = marked.parse(content, { async: false }) as string;
    } catch {
      parsed = `<p>${content}</p>`;
    }
    return enhance(DOMPurify.sanitize(parsed, SANITIZE_CONFIG), copyLabel);
  }, [content, copyLabel]);

  // One delegated listener on the container React owns, so it survives every
  // innerHTML rewrite without being re-attached per code block.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();

    const onClick = (event: MouseEvent) => {
      const button = (event.target as HTMLElement | null)?.closest<HTMLElement>('.cbw-code__copy');
      if (!button) return;

      const code = button.parentElement?.querySelector('pre');
      if (!code) return;

      void navigator.clipboard?.writeText(code.innerText).then(() => {
        button.textContent = copiedLabel;
        clearTimeout(timers.get(button));
        timers.set(
          button,
          setTimeout(() => {
            button.textContent = copyLabel;
          }, 1600),
        );
      });
    };

    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('click', onClick);
      timers.forEach(clearTimeout);
    };
  }, [copyLabel, copiedLabel]);

  return (
    <div
      ref={containerRef}
      className="cbw-markdown"
      // Sanitised above with an explicit tag/attribute allow-list.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
